<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppHeader from './components/AppHeader.vue';
import { useAuthStore } from './stores/auth';
import { useSubscription } from './composables/useSubscription';
import type { QueryParams } from './types';
import { useSubscriptionStore } from './stores/subscription';
import { storeToRefs } from 'pinia';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const subscription = useSubscription();

const subscriptionStore = useSubscriptionStore();
const { hasData, isLoading } = storeToRefs(subscriptionStore);

// Helper function to parse query params from both query string and hash fragment
function parseUrlParams(): QueryParams {
  // First try to get params from Vue Router query
  const routerQuery = route.query;

  // Also check the raw URL for query params (in case router hasn't parsed them yet)
  const urlSearchParams = new URLSearchParams(window.location.search);

  // Check hash fragment as well (iOS apps sometimes pass params in the hash)
  let hashParams = new URLSearchParams();
  const hash = window.location.hash;
  if (hash && hash.includes('?')) {
    // Extract query params from hash (e.g., #/?param=value)
    const hashQueryString = hash.split('?')[1];
    hashParams = new URLSearchParams(hashQueryString);
  } else if (hash && hash.includes('=')) {
    // Handle case where hash is just query params without path (e.g., #access_token=xxx)
    const cleanHash = hash.startsWith('#') ? hash.substring(1) : hash;
    hashParams = new URLSearchParams(cleanHash);
  }

  // Log all potential sources for debugging
  console.log('🔍 Param sources:', {
    'routerQuery': routerQuery,
    'urlSearchParams.toString()': urlSearchParams.toString(),
    'hash': hash,
    'hashParams.toString()': hashParams.toString(),
  });

  // Helper to get param from multiple sources (prioritize hash -> URL -> router)
  const getParam = (key: string): string | undefined => {
    const value = hashParams.get(key) ||
      urlSearchParams.get(key) ||
      (routerQuery[key] as string) ||
      undefined;

    // Return undefined instead of empty string
    return value && value.trim() !== '' ? value : undefined;
  };

  const params = {
    membership_id: getParam('membership_id'),
    access_token: getParam('access_token'),
    success_url: getParam('success_url'),
    cancel_url: getParam('cancel_url'),
    product_id: getParam('product_id'),
  };

  console.log('📝 parseUrlParams result:', params);
  return params;
}

// Initialize queryParams as empty - will be populated in onMounted when route is ready
const queryParams = ref<QueryParams>({
  membership_id: undefined,
  access_token: undefined,
  success_url: undefined,
  cancel_url: undefined,
  product_id: undefined,
});

onMounted(async () => {
  console.log('🔍 URL Debug Info:', {
    'window.location.href': window.location.href,
    'window.location.search': window.location.search,
    'window.location.hash': window.location.hash,
    'window.location.pathname': window.location.pathname,
    'window.location.origin': window.location.origin,
    'route.query': route.query,
    'route.fullPath': route.fullPath,
    'route.params': route.params,
    'document.referrer': document.referrer,
    'userAgent': navigator.userAgent,
  });

  // Parse params AFTER route is ready (fixes mobile Safari timing issue)
  queryParams.value = parseUrlParams();
  console.log('🚀 App mounted, query params:', queryParams.value);

  if (queryParams.value.access_token) {
    console.log('✅ Access token found, setting auth params');
    authStore.setQueryParams(queryParams.value);

    const cleanQuery = { ...route.query };
    delete cleanQuery.access_token;

    /*router.replace({
      path: route.path,
      query: Object.keys(cleanQuery).length > 0 ? cleanQuery : undefined,
    });*/

    // Wait for next tick to ensure auth store is updated
    await nextTick();
    console.log('🔄 Auth store updated, hasValidToken:', authStore.hasValidToken);

    // Don't fetch data here - let the auth watcher handle it
  } else {
    console.log('❌ No access token found in query params');
  }
});

// Watch for route changes to update queryParams if needed (e.g., navigating with new params)
// Only re-parse if there's actually an access_token in the NEW route query
watch(
  () => route.query,
  async (newQuery) => {
    // Only re-parse if the new query actually has an access_token
    // This prevents unnecessary parsing when we clean the URL
    if (newQuery.access_token && newQuery.access_token !== queryParams.value.access_token) {
      console.log('🔄 Route changed with new access_token, re-parsing params');
      const newParams = parseUrlParams();
      queryParams.value = newParams;
      authStore.setQueryParams(newParams);
      // Don't fetch data here - let the auth watcher handle it
    }
  },
);

// Single watcher for auth status to fetch data (prevents duplicate API calls)
// This will trigger when auth params are set from query params
watch(
  () => [authStore.hasValidToken, authStore.membershipId, authStore.productId],
  async ([hasToken, membershipId, productId]) => {
    console.log('🔍 Auth watcher triggered:', { hasToken, membershipId, productId, hasData: hasData.value, isLoading: isLoading.value });

    if (hasToken && membershipId && productId && !hasData.value && !isLoading.value) {
      console.log('🚀 Initializing upgrade data...');
      await subscription.initializeUpgradeData();
    } else {
      console.log('⏸️ Skipping data fetch:', {
        hasToken,
        membershipId: !!membershipId,
        productId: !!productId,
        hasData: hasData.value,
        isLoading: isLoading.value
      });
    }
  },
);
</script>

<template>
  <div class="min-h-svh flex justify-center bg-global-bg px-4 py-6">
    <div class="w-full max-w-[430px] flex flex-col flex-grow gap-y-6">
      <AppHeader :query-params="queryParams" />

      <main class="flex flex-col flex-grow gap-y-8">
        <RouterView :query-params="queryParams" />
      </main>
    </div>
  </div>
</template>

<style scoped></style>